import React from 'react'
import { Trans } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { Badge, Button, ButtonVariant, ColorVariant } from '@makyoapp/makyo-ui'

import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import Layout from '@/components/Layout'

import { billingNavLink } from '../../shared'
import AddPaymentModal from '../PaymentMethodsPage/PaymentMethodsPage'
import { useBilling } from '../Providers/BillingProviders'
import { billingRoutes } from '../routes'

export default function BillingMainPage({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { currentOrg: org } = useCurrentOrgContext()
  const { billingCycle, setBillingCycle, selectedAddons, currentPlan, updateAddonQuantity, getTotal } = useBilling()
  const { recentApps } = useOrgsContext()

  const plan = recentApps.find((o) => o.org.id === org?.id)?.plan || { name: 'FREE', badgeColor: ColorVariant.Yellow }

  const withoutSummary =
    billingRoutes.find((route) => route.withoutPaymentSummary)?.path === location.pathname.split('/').pop()

  return (
    <Layout
      title={<Trans>Billing & Subscription</Trans>}
      actions={
        <div className='flex flex-col gap-0.5'>
          <div className='flex gap-1 justify-end items-center'>
            <Trans className='text-m-sm'>
              Current Plan{' '}
              <Badge color={plan?.badgeColor as ColorVariant} className='text-xs'>
                {plan?.name}
              </Badge>
            </Trans>
          </div>
          <span className='text-xs text-gray-400 dark:text-gray-400'>
            <Trans>Your next payment is scheduled for March 22, 2025</Trans>
          </span>
        </div>
      }
      beforeContent={
        <div className='px-8'>
          <div className='mb-4'>
            <div className='border-b border-gray-200 dark:border-gray-700'>
              <nav className=' flex justify-between items-center'>
                <div className='flex gap-4'>
                  {billingRoutes.map((route) => (
                    <Link
                      key={route.path || 'index'}
                      to={billingNavLink(route.path || '', location)}
                      className={clsx(
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                        location.pathname.endsWith(route.path || '/billing')
                          ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      )}
                    >
                      <Trans>{route.name}</Trans>
                    </Link>
                  ))}
                </div>

                {!withoutSummary && (
                  <div className='flex justify-end'>
                    <div className='bg-gray-200 dark:bg-slate-600 p-1 rounded-lg inline-flex'>
                      <button
                        onClick={() => setBillingCycle('monthly')}
                        className={clsx(
                          'px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                          billingCycle === 'monthly'
                            ? 'bg-white dark:bg-slate-700 shadow-xs dark:text-white'
                            : 'bg-transparent text-gray-500 dark:text-gray-400'
                        )}
                      >
                        <Trans>Monthly</Trans>
                      </button>
                      <button
                        onClick={() => setBillingCycle('yearly')}
                        className={clsx(
                          'px-3 rounded-md text-sm font-medium transition-all flex items-center',
                          billingCycle === 'yearly'
                            ? 'bg-white dark:bg-slate-700 shadow-xs dark:text-white'
                            : 'bg-transparent text-gray-500 dark:text-gray-400'
                        )}
                      >
                        <Trans>Yearly</Trans>
                        <span className='ml-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 text-xs px-2 py-0.5 rounded-full'>
                          <Trans>Save 15%</Trans>
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </nav>
            </div>
          </div>
        </div>
      }
    >
      {children}

      {!withoutSummary && (
        <>
          <div className='bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6 mb-8'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>
              <Trans>Subscription Summary</Trans>
            </h3>

            <div className='bg-gray-50 dark:bg-slate-900 rounded-lg p-4'>
              <div className='space-y-2'>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500 dark:text-gray-400'>
                    <Trans>{currentPlan.name} Plan</Trans>
                  </span>
                  <span className='text-sm font-medium text-gray-900 dark:text-white'>
                    {billingCycle === 'yearly'
                      ? `$${currentPlan.yearlyPrice.toFixed(2)}/year`
                      : `$${currentPlan.monthlyPrice.toFixed(2)}/mo`}
                  </span>
                </div>

                {selectedAddons.map((addon) => (
                  <div key={addon.id} className='flex justify-between items-center'>
                    <span className='text-sm text-gray-500 dark:text-gray-400'>{addon.name}</span>
                    <div className='flex items-center'>
                      <div className='flex border border-gray-300 dark:border-gray-600 rounded-md mr-3  h-9'>
                        <button
                          onClick={() => updateAddonQuantity(addon.id, addon.quantity - 1)}
                          className='px-2 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-md'
                        >
                          <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 12H4' />
                          </svg>
                        </button>
                        <input
                          type='number'
                          value={addon.quantity}
                          onChange={(e) => updateAddonQuantity(addon.id, parseInt(e.target.value) || 0)}
                          className='w-10 text-center border-x border-gray-300 dark:border-gray-600 py-1 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                          min='0'
                          max='99'
                          style={{ appearance: 'textfield' }}
                        />
                        <button
                          onClick={() => updateAddonQuantity(addon.id, addon.quantity + 1)}
                          className='px-2 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-md'
                        >
                          <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                          </svg>
                        </button>
                      </div>
                      <span className='text-sm font-medium text-gray-900 dark:text-white'>
                        {billingCycle === 'yearly'
                          ? `$${(addon.price * addon.quantity * 12).toFixed(2)}/year`
                          : `$${(addon.price * addon.quantity).toFixed(2)}/mo`}
                      </span>
                    </div>
                  </div>
                ))}

                {selectedAddons.length === 0 && (
                  <div className='text-sm text-gray-500 italic'>
                    <Trans>No add-ons selected</Trans>
                  </div>
                )}

                <div className='pt-2 mt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between'>
                  <span className='text-base font-medium text-gray-900 dark:text-white'>
                    <Trans>Total {billingCycle === 'yearly' ? '(billed annually)' : '(billed monthly)'}</Trans>
                  </span>
                  <span className='text-base font-bold text-gray-900 dark:text-white'>
                    {billingCycle === 'yearly' ? `$${getTotal().toFixed(2)}/year` : `$${getTotal().toFixed(2)}/mo`}
                  </span>
                </div>
              </div>
            </div>

            <div className='mt-6 flex justify-end'>
              <Button variant={ButtonVariant.Normal} disabled={selectedAddons.length === 0}>
                <Trans>Update Subscription</Trans>
              </Button>
            </div>
          </div>

          <div>
            <div className='bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6 mb-8'>
              <div className='flex justify-between items-center mb-4'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  <Trans>Payment Method</Trans>
                </h2>

                <AddPaymentModal />
              </div>

              <div className='flex items-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg'>
                <div className='shrink-0 mr-4'>
                  <svg className='h-8 w-12 text-gray-400' viewBox='0 0 48 32' fill='currentColor'>
                    <rect width='48' height='32' rx='4' fill='currentColor' fillOpacity='0.2' />
                    <path d='M32 20H26V22H32V20Z' fill='currentColor' />
                    <path d='M36 16H24V18H36V16Z' fill='currentColor' />
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    <Trans>Visa ending in 4242</Trans>
                  </p>
                  <p className='text-sm text-gray-500'>
                    <Trans>Expires 12/2028</Trans>
                  </p>
                </div>
                <div className='ml-auto'>
                  <Button variant={ButtonVariant.OutlinedGray}>
                    <Trans>Edit</Trans>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6 mb-8'>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
              <Trans>Frequently Asked Questions</Trans>
            </h2>

            <div className='space-y-4'>
              <div>
                <h3 className='text-base font-medium text-gray-900 dark:text-white'>
                  <Trans>What happens when I upgrade my plan?</Trans>
                </h3>
                <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                  <Trans>
                    When you upgrade, you&apos;ll have immediate access to all new features and increased limits.
                    We&apos;ll prorate your billing so you only pay for the time you&apos;re on the new plan.
                  </Trans>
                </p>
              </div>

              <div>
                <h3 className='text-base font-medium text-gray-900 dark:text-white'>
                  <Trans>Can I downgrade at any time?</Trans>
                </h3>
                <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                  <Trans>
                    Yes, you can downgrade at any time. Changes will take effect at the end of your current billing
                    cycle. Make sure your usage fits within the lower plan&apos;s limits before downgrading.
                  </Trans>
                </p>
              </div>
            </div>
          </div>

          <div className='bg-gray-50 dark:bg-gray-900 rounded-xl p-6 text-center'>
            <h3 className='text-base font-medium text-gray-900 dark:text-white mb-2'>
              <Trans>Need help with your billing?</Trans>
            </h3>
            <p className='text-sm text-gray-500 dark:text-gray-400 mb-4'>
              <Trans>Our support team is available to help with any billing questions you might have.</Trans>
            </p>
            <Button variant={ButtonVariant.Normal} className='mx-auto'>
              <Trans>Contact Support</Trans>
            </Button>
          </div>
        </>
      )}
    </Layout>
  )
}
